import { MenuItem, MenuList } from "@mui/material";
import type { RenderFilterProps } from "@shared/frontend/hyper-table/filter-types";
import { useHyperTable } from "@shared/frontend/hyper-table/HyperTable";
import { generateHyperFilterFromPath } from "@shared/helpers/sql-relations";
import React, { useCallback } from "react";
import type { EntityFilter } from "remult";
import CustomFilter from "./CustomFilter";
import type { EntityFilterProps } from "./helpers";
import { CheckboxIcon } from "@shared/frontend/icons";

type BooleanFilterProps<Entity> = EntityFilterProps<Entity>

const BooleanFilter = <Entity extends any>(
  props: BooleanFilterProps<Entity>,
) => {
  const { filter, closeMenu } = props;
  const { getEntity } = useHyperTable<Entity>();

  const handleChange = useCallback(
    (onSave: RenderFilterProps<Entity>["onSave"], value: boolean) => {
      const entity = getEntity();
      const isRelationalFilter =
        "hyperFilter" in entity && filter.fieldName.includes(".");
      onSave(
        (prev) => {
          return {
            meta: {
              ...prev.meta,
              value: value === prev.meta.value ? undefined : value,
            },
            value:
              value === prev.meta.value
                ? {}
                : isRelationalFilter
                  ? (entity as any).hyperFilter(
                    generateHyperFilterFromPath(
                      entity,
                      filter.fieldName,
                      value,
                    ),
                  )
                  : ({
                    [filter.fieldName]: value,
                  } as EntityFilter<Entity>),
          };
        },
        { closeOnSave: false },
      );
    },
    [getEntity(), filter.fieldName],
  );

  return (
    <CustomFilter<Entity>
      closeMenu={closeMenu}
      filter={{
        ...filter,
        icon: "boolean",
        renderFilter: useCallback(
          ({ meta, onSave }) => (
            <MenuList>
              <MenuItem
                key="true"
                sx={{ gap: "0.8rem" }}
                onClick={() => handleChange(onSave, true)}
              >
                <CheckboxIcon
                  checked={meta.value === true}
                  className="size-4"
                  aria-hidden="true"
                />
                <span>כן</span>
              </MenuItem>
              <MenuItem
                key="false"
                sx={{ gap: "0.8rem" }}
                onClick={() => handleChange(onSave, false)}
              >
                <CheckboxIcon
                  checked={meta.value === false}
                  className="size-4"
                  aria-hidden="true"
                />
                <span>לא</span>
              </MenuItem>
            </MenuList>
          ),
          [filter.fieldName, handleChange],
        ),
      }}
    />
  );
};

export default BooleanFilter;
